import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, Form } from 'react-bootstrap';
import Select from 'react-select';
import { Formik, Field } from 'formik';

import {
  reviewReaders as readersDuck,
  users as usersDuck
} from 'ducks';
import { duckify, filterDuck } from 'utils/duckify';
import Placeholder from 'components/Placeholder';
import Utility from '../../../../legacy/helpers/shared/utility';
import Routes from '../../../../routes';
import './styles/BulkManageReaders.scss';

const normalizeResponseData = (data) => data[0];

const BulkManageReaders = ({
  className,
  schoolYearId,
  contactIds,
  handleClose,
  saveReaders,
  allDucksReady,
  readers
}) => {
  const [saving, setSaving] = useState(false);

  const functionName = (values, formikBag) => {
    const data = {
      assign: values.action === 'assign' ? 'true' : 'false',
      contact_ids: contactIds,
      school_year_id: schoolYearId,
      user_ids: values.readers.map(reader => reader.id)
    };
    setSaving(true);
    saveReaders(data)
      .then(res => {
        Utility.successMessage(res.message);
        // Wait a little bit and then reload the page to refresh contact list data.
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      })
      .catch(res => {
        if (res.error) {
          Utility.errorMessage(
            (
              res.error.match(/Please only select contacts/) || res.error.match(/term is not setup for reviews/)
                ? res.error : "We're sorry, an error has occurred. If the problem persists, please contact support."
            )
          );
        }
      })
      .then(() => {
        setSaving(false);
        formikBag.setSubmitting(false);
        handleClose();
      });
  };

  return (
    <Formik
      initialValues={{
        action: 'assign',
        readers: []
      }}
      validate={values => {
        const errors = {};
        if (values.readers.length === 0) {
          errors.readers = 'You must select at least 1 reader';
        }

        return errors;
      }}
      onSubmit={functionName}
    >
      {({
        values,
        errors,
        setFieldValue,
        handleSubmit
      }) => (
        <Modal
          show
          className={className}
          animation={false}
          backdrop='static'
          keyboard={false}
        >
          <Modal.Header>
            <Modal.Title>Manage Readers</Modal.Title>
          </Modal.Header>
          <Modal.Body className='manage-readers-modal-body'>
            <Placeholder.loading ready={allDucksReady}>
              <p>
                Assign or remove readers for&nbsp;
                <strong>{contactIds.length}</strong>
                &nbsp;selected contacts.
              </p>
              <Form noValidate className='manage-readers-modal__form'>
                <Form.Group className='manage-readers-modal__form-group'>
                  <Form.Label className='manage-readers-modal__label'>Action</Form.Label>
                  <div className='manage-readers-modal__control'>
                    <Field
                      as={Form.Check}
                      id='action-assign'
                      type='radio'
                      label='Assign'
                      name='action'
                      value='assign'
                    />
                    <Field
                      as={Form.Check}
                      id='action-remove'
                      type='radio'
                      label='Remove'
                      name='action'
                      value='remove'
                    />
                  </div>
                </Form.Group>
                <Form.Group className='manage-readers-modal__form-group'>
                  <Form.Label className='manage-readers-modal__label'>Readers</Form.Label>
                  <div className='manage-readers-modal__select'>
                    <Field
                      as={Select}
                      removeSelected={false}
                      closeOnSelect={false}
                      className={errors.readers ? 'is-invalid' : ''}
                      multi
                      placeholder='Select Readers'
                      noResultsText='No Readers Found'
                      options={readers}
                      labelKey='name'
                      valueKey='id'
                      value={values.readers}
                      onChange={selected => setFieldValue('readers', selected)}
                    />
                    <Form.Control.Feedback type='invalid'>{errors.readers}</Form.Control.Feedback>
                  </div>
                </Form.Group>
              </Form>
              {readers.length === 0 && (
                <p className='error'>
                  Readers cannot be assigned at this time. One or more users must be given the&nbsp;
                  <em>reader</em>
                  &nbsp;permission in&nbsp;
                  <a href={Routes.admins_path()}>
                    Admin Accounts
                  </a>
                  &nbsp;under setup.
                </p>
              )}
              {readers.length > 0 && (
                <div>
                  {values.action === 'assign' && (
                    <p>
                      Readers will be notified by email.
                    </p>
                  )}
                  {values.action === 'remove' && (
                    <p>
                      The reader(s)&apos; comments and scores will no longer appear on the candidate(s)&apos; record.
                      <em>
                        &nbsp;
                        If you reassign the reader(s) to the contact(s) later, any comments and scores will be restored.
                      </em>
                    </p>
                  )}
                </div>
              )}
            </Placeholder.loading>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant='light'
              onClick={handleClose}
            >
              Close
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Formik>
  );
};

BulkManageReaders.propTypes = {
  /** className prop */
  className: PropTypes.string,

  /** ID of currently selected school year */
  schoolYearId: PropTypes.string,

  /** ID List of currently selected contacts */
  contactIds: PropTypes.arrayOf(PropTypes.string),

  /** allDucksReady bool */
  allDucksReady: PropTypes.bool.isRequired,

  /** review readers duck */
  readers: PropTypes.arrayOf(
    PropTypes.shape({})
  ).isRequired,

  /** saveReaders POST api call to update readers */
  saveReaders: PropTypes.func.isRequired,

  /** handleClose callback function */
  handleClose: PropTypes.func.isRequired
};

BulkManageReaders.defaultProps = {
  className: '',
  schoolYearId: '',
  contactIds: []
};

const mapDucksToProps = () => ({
  readers: filterDuck(usersDuck, { reader_permitted: 1 })
});

const mapDispatchToProps = dispatch => ({
  saveReaders: data => dispatch(readersDuck.actions.create(data))
    .then(readersDuck.actions.denormalizeObjects)
    .then(normalizeResponseData)
});

export default duckify(mapDucksToProps, () => {}, mapDispatchToProps)(BulkManageReaders);
